/**
 * The construction-scene panel (feature 0059 lot 1d) — the seller-facing face of a generation run.
 *
 * Three deliberate differences from the Lightfunnels bar this was analysed against (0059 §5):
 *  - the étiquette SAYS THE WORK ("Écrit les questions fréquentes…"), never a generic spinner — the
 *    label naming the work is what separates intelligent from slow;
 *  - NOTHING here blocks the editor: the canvas stays live behind the panel, the seller can scroll and
 *    click mid-run (their bar greys the world out and that is what turns a demo into a waiting room);
 *  - the run ends with the "à toi de remplir" list — the integrity skips (reviews, studies,
 *    comparisons) are handed to the seller instead of shipping invented ones.
 */
import { useState } from 'react';
import { bt, btp } from '../i18n';
import { getRecipe } from '@aquilixshop/ui-react';
import { useGenerationStore } from '../state/generation/generationStore';

/** Reader-language names for the section types the integrity rule reserves to the seller. */
const INTEGRITY_LABELS: Record<string, string> = {
  testimonials: bt('chrome.gen.int_testimonials', 'Customer reviews'),
  'trustpilot-reviews': bt('chrome.gen.int_trustpilot', 'Trustpilot reviews'),
  'facebook-testimonials': bt('chrome.gen.int_facebook', 'Facebook reviews'),
  results: bt('chrome.gen.int_results', 'Numbers and results'),
  'spec-comparison': bt('chrome.gen.int_comparison', 'The comparison table'),
  'comparison-table': bt('chrome.gen.int_comparison', 'The comparison table'),
  'custom-html': bt('chrome.gen.int_html', 'The custom HTML block'),
};

export function GenerationPanel() {
  const status = useGenerationStore((s) => s.status);
  const categoryId = useGenerationStore((s) => s.categoryId);
  const activity = useGenerationStore((s) => s.activity);
  const currentPage = useGenerationStore((s) => s.currentPage);
  const sections = useGenerationStore((s) => s.sections);
  const summary = useGenerationStore((s) => s.summary);
  const integritySkips = useGenerationStore((s) => s.integritySkips);
  const start = useGenerationStore((s) => s.start);
  const dismiss = useGenerationStore((s) => s.dismiss);
  const initialBrief = useGenerationStore((s) => s.initialBrief);
  // DERIVED, not copied: the field shows the dashboard card's phrase until the seller edits it —
  // `null` means "untouched". No effect, no ref, and a re-arm with a new phrase shows the new phrase.
  const [edited, setEdited] = useState<string | null>(null);
  const description = edited ?? initialBrief;

  const mode = useGenerationStore((s) => s.mode);
  if (status === 'idle' || mode === 'takeover') return null; // the takeover owns that flow entirely
  const recipe = categoryId ? getRecipe(categoryId) : null;

  if (status === 'armed') {
    return (
      <aside
        className="bld-gen"
        role="dialog"
        aria-label={bt('chrome.gen.aria', 'Writing your texts')}
      >
        <div className="bld-gen__card">
          <p className="bld-gen__kicker">{recipe?.label ?? bt('chrome.gen.store', 'Store')}</p>
          <h2 className="bld-gen__title">{bt('chrome.gen.title', 'Write your store’s texts')}</h2>
          <p className="bld-gen__hint">
            {bt(
              'chrome.gen.hint',
              'The theme is in place. The AI writes the texts of your product, home and collection pages — the design does not move, and you can retouch or undo (Ctrl-Z) everything after.',
            )}
          </p>
          <label className="bld-gen__label" htmlFor="bld-gen-desc">
            {bt('chrome.gen.describe', 'Describe your store in one sentence')}
          </label>
          <textarea
            id="bld-gen-desc"
            className="bld-gen__input"
            rows={2}
            maxLength={600}
            placeholder={bt(
              'chrome.gen.describe_ph',
              'e.g. Artisan coffee roaster in Casablanca, single-origin subscriptions.',
            )}
            value={description}
            onChange={(e) => setEdited(e.target.value)}
          />
          <div className="bld-gen__actions">
            <button type="button" className="bld-gen__btn bld-gen__btn--ghost" onClick={dismiss}>
              {bt('chrome.gen.later', 'Later')}
            </button>
            <button
              type="button"
              className="bld-gen__btn"
              onClick={() => void start(description.trim())}
            >
              {bt('chrome.gen.start', 'Write the texts')}
            </button>
          </div>
        </div>
      </aside>
    );
  }

  if (status === 'running') {
    const done = sections.filter((s) => s.state === 'done').length;
    const failed = sections.filter((s) => s.state === 'failed').length;
    return (
      <aside className="bld-gen bld-gen--bar" role="status" aria-live="polite">
        <div className="bld-gen__bar">
          <span className="bld-gen__spinner" aria-hidden="true" />
          <div className="bld-gen__barText">
            <strong>{activity ?? bt('chrome.gen.running', 'Writing…')}</strong>
            {currentPage && (
              <span className="bld-gen__page">
                {currentPage}
                {done > 0 &&
                  ` · ${btp('chrome.gen.written_n', '{count} sections written', { count: done })}`}
                {failed > 0 &&
                  ` · ${btp('chrome.gen.kept_n', '{count} kept from the theme', { count: failed })}`}
              </span>
            )}
          </div>
        </div>
      </aside>
    );
  }

  // done | unavailable
  return (
    <aside
      className="bld-gen"
      role="dialog"
      aria-label={bt('chrome.gen.done_aria', 'Writing finished')}
    >
      <div className="bld-gen__card">
        {status === 'unavailable' ? (
          <>
            <h2 className="bld-gen__title">
              {bt('chrome.gen.unavailable', 'Generation isn’t available here')}
            </h2>
            <p className="bld-gen__hint">
              {bt(
                'chrome.gen.unavailable_hint',
                'This environment has no generation key. The theme is in place with its original texts — everything stays editable by hand.',
              )}
            </p>
          </>
        ) : (
          <>
            <h2 className="bld-gen__title">{bt('chrome.gen.done', 'Your texts are written')}</h2>
            <p className="bld-gen__hint">
              {btp('chrome.gen.done_count', '{count} sections written for your store.', {
                count: summary?.sectionsWritten ?? 0,
              })}
              {summary && summary.sectionsFailed > 0 && (
                <>
                  {' '}
                  {btp(
                    'chrome.gen.done_kept',
                    '{count} kept the theme’s text — you can regenerate them later.',
                    {
                      count: summary.sectionsFailed,
                    },
                  )}
                </>
              )}{' '}
              {bt('chrome.gen.done_undo', 'Each section undoes with Ctrl-Z like any other change.')}
            </p>
            {integritySkips.length > 0 && (
              <div className="bld-gen__integrity">
                <p className="bld-gen__label">
                  {bt('chrome.gen.integrity', 'Yours to fill in — we never invent:')}
                </p>
                <ul>
                  {[...new Set(integritySkips.map((t) => INTEGRITY_LABELS[t] ?? t))].map(
                    (label) => (
                      <li key={label}>{label}</li>
                    ),
                  )}
                </ul>
              </div>
            )}
          </>
        )}
        <div className="bld-gen__actions">
          <button type="button" className="bld-gen__btn" onClick={dismiss}>
            {bt('chrome.gen.continue', 'Continue in the editor')}
          </button>
        </div>
      </div>
    </aside>
  );
}
